<!--
  Список разговоров: показывает состояние чатов и сообщает наверх, какой чат выбран.
-->
<template>
  <div class="chat-list-wrapper">
    <div class="chat-list">
      <div v-if="conversations.length === 0" class="empty-chats">
        <AppIcon name="comment" :size="44" />
        <p>Чатов пока нет</p>
        <span>Начните общение, и чаты появятся здесь</span>
      </div>
      <ul v-else class="chats">
        <li
          v-for="conversation in conversations"
          :key="conversation.id"
          class="chat-item"
          :class="{
            active: selectedChatId === conversation.id,
            'new-chat': conversation.isNew,
          }"
          @click="$emit('select', conversation.id)"
        >
          <div class="chat-avatar">
            <img
              v-if="conversationAvatarUrl(conversation)"
              :src="conversationAvatarUrl(conversation)"
              :alt="conversation.name || `Чат ${conversation.id}`"
              class="avatar-image"
            />
            <div
              v-else
              class="avatar-gradient"
              :style="
                getAvatarStyle(conversation.name || conversation.id)
              "
            >
              {{
                getAvatarInitials(
                  conversation.name || `Чат ${conversation.id}`
                )
              }}
            </div>
            <span v-if="conversation.isNew" class="new-badge">новый</span>
          </div>
          <div class="chat-info">
            <div class="chat-header">
              <span class="chat-name">
                {{ conversation.name || `Чат #${conversation.id}` }}
              </span>
              <span
                v-if="lastMessageTime(conversation)"
                class="chat-time"
              >
                {{ lastMessageTime(conversation) }}
              </span>
            </div>
            <p
              v-if="conversation.last_message_text"
              class="chat-last-message"
            >
              {{ conversation.last_message_text }}
            </p>
            <span
              v-if="conversation.unread_messages_count"
              class="unread-badge"
            >
              {{ unreadCountLabel(conversation.unread_messages_count) }}
            </span>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import AppIcon from "@/components/Icon/AppIcon.vue";
import {
  getAvatarStyle,
  getAvatarInitials,
} from "@/utils/chatUtils";
import { unreadCountBadgeLabel } from "@/utils/chatUnreadUtils";
import { formatDateAndTime } from "@/utils/dateUtils";
import { resolveUrl } from "@/utils/fileUtils";

export default {
  name: "ChatList",

  components: {
    AppIcon,
  },

  props: {
    conversations: {
      type: Array,
      default: () => [],
    },
    selectedChatId: {
      type: Number,
      default: null,
    },
  },

  emits: ["select"],

  methods: {
    getAvatarStyle,
    getAvatarInitials,

    conversationAvatarUrl(conversation) {
      return resolveUrl(conversation?.avatar_url);
    },

    lastMessageTime(conversation) {
      const value = conversation?.last_message_created_at;
      if (!value) return "";

      return formatDateAndTime(value);
    },

    unreadCountLabel(count) {
      return unreadCountBadgeLabel(count);
    },
  },
};
</script>

<style scoped>
.chat-list-wrapper {
  width: 30%;
  max-width: 320px;
  min-width: 150px;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.chat-list {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.empty-chats {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 40px 20px;
  text-align: center;
  color: #999;
}

.empty-chats p {
  font-size: 16px;
  font-weight: 500;
  margin: 16px 0 8px 0;
  color: #666;
}

.empty-chats span {
  font-size: 13px;
  line-height: 1.4;
}

.chats {
  list-style: none;
  padding: 0;
  margin: 0;
  overflow-y: auto;
  flex: 1;
}

.chat-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #eaeaea;
  background-color: #fff;
  cursor: pointer;
  transition: background-color 0.2s ease;
  gap: 12px;
  position: relative;
}

.chat-item:hover {
  background-color: #e6e6e6;
}

.chat-item.active {
  background-color: #e8f5e8;
}

.chat-item.new-chat {
  background-color: #f0f8ff;
}

.chat-item.new-chat::after {
  content: "";
  position: absolute;
  left: 4px;
  top: 50%;
  transform: translateY(-50%);
  width: 6px;
  height: 6px;
  background: #3b82f6;
  border-radius: 50%;
}

.chat-avatar {
  flex-shrink: 0;
  position: relative;
}

.avatar-gradient {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 14px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.avatar-image {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.new-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  background: #ef4444;
  color: white;
  font-size: 9px;
  padding: 2px 5px;
  border-radius: 8px;
  font-weight: 600;
  line-height: 1;
}

.chat-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
  position: relative;
}

.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.chat-name {
  font-weight: 500;
  color: #333;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.chat-time {
  color: #888;
  font-size: 11px;
  line-height: 1;
  white-space: nowrap;
  flex-shrink: 0;
}

.chat-last-message {
  color: #666;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
  line-height: 1.3;
  padding-right: 36px;
}

.unread-badge {
  position: absolute;
  right: 0;
  bottom: 0;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: #3b82f6;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  line-height: 20px;
  text-align: center;
}

.chat-item.new-chat .chat-name {
  font-weight: 600;
}

.chat-item.new-chat .chat-last-message {
  color: #3b82f6;
}

@media (max-width: 768px) {
  .chat-list-wrapper {
    width: 100%;
    max-width: none;
    min-width: 0;
    height: 100%;
  }
}
</style>
